import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { Loader2, Calendar as CalendarIcon, Plane, RotateCcw, Clock, X, Save } from "lucide-react";
import { format, addDays, startOfWeek, isSameDay } from "date-fns";

const HORIZON_DAYS = 60; // ~2 months
const STORAGE_REASON = "Blocked (day off)";

// Working day: 1 PM - 7 PM in 1-hour bins. Marlowe can block any of these.
const HOURS = Array.from({ length: 6 }, (_, i) => 13 + i); // [13..18]
const OP_START = HOURS[0];
const OP_END = HOURS[HOURS.length - 1] + 1;

function hourLabel(h) {
  const d = new Date();
  d.setHours(h, 0, 0, 0);
  return format(d, "h a");
}

function rangesFromBlockedHours(blocks) {
  // Convert API format [[9,12], [14,15]] → flat set of blocked start hours,
  // clamped to the current operating window so legacy blocks (8–10 AM etc.)
  // are silently dropped the next time the admin opens the modal.
  const set = new Set();
  for (const [s, e] of blocks || []) {
    const start = Math.max(s, OP_START);
    const end = Math.min(e, OP_END);
    for (let h = start; h < end; h++) set.add(h);
  }
  return set;
}

function countBlockedInWindow(blocks) {
  // Sum only the portion of each range that overlaps the operating window.
  let total = 0;
  for (const [s, e] of blocks || []) {
    const start = Math.max(s, OP_START);
    const end = Math.min(e, OP_END);
    if (end > start) total += end - start;
  }
  return total;
}

function blockedHoursFromSet(set) {
  // Convert flat set {9,10,11,14} → [[9,12], [14,15]]
  const sorted = Array.from(set).sort((a, b) => a - b);
  const out = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i];
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    out.push([start, sorted[j] + 1]);
    i = j + 1;
  }
  return out;
}

/**
 * Owner-facing availability calendar with day-level AND hour-level blocking.
 * Click a day to open a modal with hourly checkboxes.
 */
export default function AvailabilityTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockedMap, setBlockedMap] = useState({}); // {date: reason}
  const [hourBlocksMap, setHourBlocksMap] = useState({}); // {date: [[s,e],...]}
  const [bookingsMap, setBookingsMap] = useState({}); // {date: hoursBooked}
  const [reason, setReason] = useState(STORAGE_REASON);
  const [modalDate, setModalDate] = useState(null);
  const [modalHours, setModalHours] = useState(new Set());
  const [modalSaving, setModalSaving] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const grid = useMemo(() => {
    const start = startOfWeek(today, { weekStartsOn: 1 });
    return Array.from({ length: HORIZON_DAYS + 14 }, (_, i) => addDays(start, i));
  }, [today]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: avail }, { data: bookings }] = await Promise.all([
        api.get("/admin/availability", { params: { days: HORIZON_DAYS + 14 }, withCredentials: true }),
        api.get("/bookings", { withCredentials: true }),
      ]);
      const bmap = {};
      for (const b of (avail.blocked_dates || [])) bmap[b.date] = b.reason || "Blocked";
      setBlockedMap(bmap);

      const hbmap = {};
      for (const h of (avail.hour_blocks || [])) hbmap[h.date] = h.blocked_hours || [];
      setHourBlocksMap(hbmap);

      const hmap = {};
      const liveStatuses = new Set(["pending", "confirmed"]);
      for (const b of bookings || []) {
        if (!liveStatuses.has(b.status)) continue;
        const dates = (b.scheduled_dates && b.scheduled_dates.length) ? b.scheduled_dates : [b.date];
        const hoursPerDay = Number(b.daily_hours_high || b.duration_hours || 0);
        for (const d of dates) {
          if (!d) continue;
          hmap[d] = (hmap[d] || 0) + hoursPerDay;
        }
      }
      setBookingsMap(hmap);
    } catch {
      toast.error("Could not load availability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Click handler — short click = open hour modal; cmd/ctrl-click = quick day toggle.
  const openHourEditor = (dateStr) => {
    setModalDate(dateStr);
    setModalHours(rangesFromBlockedHours(hourBlocksMap[dateStr]));
  };

  const toggleDayBlock = async (dateStr) => {
    const isBlocked = !!blockedMap[dateStr];
    setSaving(true);
    try {
      await api.put(
        "/admin/availability",
        { dates: [dateStr], blocked: !isBlocked, reason: !isBlocked ? reason : null },
        { withCredentials: true },
      );
      if (isBlocked) {
        const next = { ...blockedMap };
        delete next[dateStr];
        setBlockedMap(next);
        toast.success("Day reopened");
      } else {
        setBlockedMap({ ...blockedMap, [dateStr]: reason });
        toast.success("Day blocked");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const saveHourBlocks = async () => {
    setModalSaving(true);
    try {
      const blocked_hours = blockedHoursFromSet(modalHours);
      await api.put(
        "/admin/availability/hours",
        { date: modalDate, blocked_hours, reason: reason || "Unavailable" },
        { withCredentials: true },
      );
      const next = { ...hourBlocksMap };
      if (blocked_hours.length) next[modalDate] = blocked_hours;
      else delete next[modalDate];
      setHourBlocksMap(next);
      toast.success(blocked_hours.length ? "Hours blocked" : "Hours cleared");
      setModalDate(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setModalSaving(false);
    }
  };

  return (
    <div data-testid="availability-tab" className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" /> Availability calendar
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            <strong>Click a day</strong> to block specific hours (e.g. 1–3 PM only).{" "}
            <strong>Shift-click</strong> to block the whole day.
            The smart scheduler caps each day at 6 hours of work and subtracts any blocked hours.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1">Reason (shown internally)</label>
            <Input
              data-testid="block-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-10 w-56"
              placeholder="Vacation, family, etc."
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="availability-refresh"
                  className="h-10 rounded-full" disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      <Card className="p-5 lg:p-7 bg-white border-border">
        <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
          <LegendDot color="bg-white border border-border" label="Open" />
          <LegendDot color="bg-rose-100 border border-rose-300" label="Blocked day" />
          <LegendDot color="bg-amber-100 border border-amber-300" label="Some hours blocked" />
          <LegendDot color="bg-emerald-100 border border-emerald-300" label="Has bookings" />
          <LegendDot color="bg-muted/50" label="Past" />
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-muted-foreground text-center py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5" data-testid="availability-grid">
          {grid.map((d) => {
            const iso = format(d, "yyyy-MM-dd");
            const past = d < today;
            const isToday = isSameDay(d, today);
            const blockedReason = blockedMap[iso];
            const isBlocked = !!blockedReason;
            const hourBlocks = hourBlocksMap[iso] || [];
            const hourBlocksCount = countBlockedInWindow(hourBlocks);
            const hoursBooked = bookingsMap[iso] || 0;
            const partial = !isBlocked && hourBlocksCount > 0;

            let cls = "bg-white border border-border hover:border-primary";
            if (past) cls = "bg-muted/40 border border-border text-muted-foreground cursor-not-allowed opacity-60";
            else if (isBlocked) cls = "bg-rose-100 border border-rose-300 hover:bg-rose-200";
            else if (partial) cls = "bg-amber-50 border border-amber-300 hover:border-amber-400";
            else if (hoursBooked > 0) cls = "bg-emerald-50 border border-emerald-300 hover:border-emerald-400";

            return (
              <button
                key={iso}
                type="button"
                disabled={past || saving}
                onClick={(e) => {
                  if (past) return;
                  if (e.shiftKey) toggleDayBlock(iso);
                  else openHourEditor(iso);
                }}
                data-testid={`avail-day-${iso}`}
                className={`relative rounded-xl p-2.5 text-left transition-all min-h-[76px] ${cls} ${
                  isToday ? "ring-2 ring-primary/40" : ""
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{format(d, "EEE")}</div>
                <div className="font-display text-xl font-semibold leading-none mt-0.5">{format(d, "d")}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{format(d, "MMM")}</div>
                {isBlocked && (
                  <Badge variant="outline" className="absolute top-1.5 right-1.5 px-1.5 py-0 text-[9px] bg-rose-50 border-rose-300 text-rose-700">
                    <Plane className="w-2.5 h-2.5" />
                  </Badge>
                )}
                {!isBlocked && partial && (
                  <span data-testid={`avail-hours-blocked-${iso}`} className="absolute top-1.5 right-1.5 text-[9px] font-semibold text-amber-700 flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" /> -{hourBlocksCount}h
                  </span>
                )}
                {!isBlocked && !partial && hoursBooked > 0 && (
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-semibold text-emerald-700">{hoursBooked}h</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-5">
          Tip: type a reason above before opening a day. It's internal — customers only see "Unavailable".
        </p>
      </Card>

      {/* Hourly block modal */}
      <Dialog open={!!modalDate} onOpenChange={(o) => !o && setModalDate(null)}>
        <DialogContent data-testid="hour-block-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Block hours · {modalDate ? format(new Date(modalDate + "T00:00:00"), "EEEE, MMM d") : ""}</DialogTitle>
            <DialogDescription>
              Pick the hours you're unavailable. Customers won't be able to book this day if the remaining hours can't fit their job.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 my-2" data-testid="hour-grid">
            {HOURS.map((h) => {
              const on = modalHours.has(h);
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => {
                    const next = new Set(modalHours);
                    if (on) next.delete(h); else next.add(h);
                    setModalHours(next);
                  }}
                  data-testid={`hour-toggle-${h}`}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    on
                      ? "border-rose-400 bg-rose-50 text-rose-800"
                      : "border-border bg-white hover:border-primary/40"
                  }`}
                >
                  <span className="font-medium">{hourLabel(h)} – {hourLabel(h + 1)}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{on ? "Blocked" : "Open"}</span>
                </button>
              );
            })}
          </div>

          {modalDate && blockedMap[modalDate] && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
              Heads-up: the whole day is already blocked. Use Shift-click on the day to unblock.
            </p>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setModalHours(new Set())}
              data-testid="hour-clear-all"
              className="rounded-full"
            >
              <X className="w-4 h-4 mr-1.5" /> Clear all
            </Button>
            <Button
              onClick={saveHourBlocks}
              disabled={modalSaving}
              data-testid="hour-save"
              className="btn-primary rounded-full"
            >
              {modalSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <span className={`inline-block w-3 h-3 rounded ${color}`} /> {label}
    </span>
  );
}
